(()=>{'use strict';
const root=()=>window.Nylura?.root||'/';const endpoint=p=>root()+p;let cartState=null,activeRequest=false,lastFocus=null;
const money=cents=>new Intl.NumberFormat('de-DE',{style:'currency',currency:window.Nylura?.currency||'EUR'}).format(cents/100);
const escapeHTML=value=>String(value??'').replace(/[&<>"']/g,c=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
async function request(path,options={}){const response=await fetch(endpoint(path),{headers:{'Content-Type':'application/json','Accept':'application/json'},...options});const data=await response.json();if(!response.ok)throw Error(data.description||data.message||'Bitte versuche es erneut.');return data;}
function cartOpen(){lastFocus=document.activeElement;document.querySelector('[data-cart-overlay]').hidden=false;const drawer=document.querySelector('[data-cart-drawer]');drawer.classList.add('is-open');drawer.setAttribute('aria-hidden','false');document.body.style.overflow='hidden';drawer.focus();updateCartTimer();}
function cartClose(){document.querySelector('[data-cart-overlay]').hidden=true;const drawer=document.querySelector('[data-cart-drawer]');drawer.classList.remove('is-open');drawer.setAttribute('aria-hidden','true');document.body.style.overflow='';lastFocus?.focus();}
function cartCents(value, fallback = 0) {
  if (value == null) return fallback;
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, Math.round(number)) : fallback;
}
/* Nylura managed gift reconciliation: existing zero-price gift lines only. */
const nyluraManagedGift = Object.freeze({
  offerId: 'cmv3ohvx708jyobkplxyqjgdd',
  variantId: '50660599824601',
  packageVariantIds: ['50660582752473', '50660582785241']
});
let nyluraGiftSyncQueue = Promise.resolve();
function nyluraBundleMetadata(item) {
  let value = item?.properties?.__mojo_bundle;
  if (typeof value === 'string') { try { value = JSON.parse(value); } catch { return null; } }
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
}
function nyluraCartQuantity(item) {
  const quantity = Number(item?.quantity);
  return Number.isSafeInteger(quantity) && quantity >= 0 ? quantity : null;
}
function nyluraQualifyingPackage(item) {
  return nyluraManagedGift.packageVariantIds.includes(String(item?.variant_id ?? item?.id)) &&
    nyluraCartQuantity(item) != null && !item?.selling_plan_allocation;
}
function nyluraManagedGiftKind(item) {
  if (!item?.key || String(item.variant_id ?? item.id) !== nyluraManagedGift.variantId) return null;
  const finalPrice = Number(item.final_price ?? item.price);
  if (!Number.isFinite(finalPrice) || finalPrice !== 0 ||
      (item.final_line_price != null && Number(item.final_line_price) !== 0) ||
      nyluraCartQuantity(item) == null || item.selling_plan_allocation) return null;
  const bundle = nyluraBundleMetadata(item);
  if (bundle?.role === 'gift' && bundle.deal === nyluraManagedGift.offerId &&
      typeof bundle.id === 'string' && bundle.id.trim()) return 'mojo';
  if ((item.properties?.__mojo_bundle == null || item.properties.__mojo_bundle === '') &&
      String(item.properties?._nylura_gift) === 'true') return 'native';
  return null;
}
function planNyluraGiftUpdates(cart) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const byKey = new Map(items.filter(item => item?.key).map(item => [item.key, item]));
  const groups = new Map();
  for (const gift of items) {
    const kind = nyluraManagedGiftKind(gift);
    if (!kind) continue;
    const meta = nyluraBundleMetadata(gift);
    const parentKey = gift.parent_relationship?.parent_key;
    let groupKey, wanted;
    if (typeof parentKey === 'string' && parentKey) {
      const parent = byKey.get(parentKey);
      // Respect Shopify's actual parent. Never borrow entitlement from another line/instance.
      if (!parent || !nyluraQualifyingPackage(parent)) continue;
      if (kind === 'mojo') {
        const parentMeta = nyluraBundleMetadata(parent);
        if (parentMeta?.main !== true || parentMeta.deal !== meta.deal || parentMeta.id !== meta.id) continue;
      }
      groupKey = 'parent:' + parentKey;
      wanted = nyluraCartQuantity(parent);
    } else if (gift.parent_relationship != null) {
      // Unknown relationship shapes are deliberately not interpreted as a flat gift.
      continue;
    } else if (kind === 'mojo') {
      const parents = items.filter(item => {
        const parentMeta = nyluraBundleMetadata(item);
        return parentMeta?.main === true && parentMeta.deal === meta.deal && parentMeta.id === meta.id;
      });
      // Only a unique matching main can identify the gift's entitlement without a parent key.
      if (parents.length !== 1 || !nyluraQualifyingPackage(parents[0])) continue;
      groupKey = 'parent:' + parents[0].key;
      wanted = nyluraCartQuantity(parents[0]);
    } else {
      // Legacy native gifts are cart-wide; Mojo parents already have their own independent gifts.
      const parents = items.filter(item => nyluraQualifyingPackage(item) &&
        (item.properties?.__mojo_bundle == null || item.properties.__mojo_bundle === ''));
      groupKey = 'native-legacy';
      wanted = parents.reduce((total, parent) => total + nyluraCartQuantity(parent), 0);
    }
    if (!Number.isSafeInteger(wanted) || wanted < 0) continue;
    if (!groups.has(groupKey)) groups.set(groupKey, { wanted, gifts: [] });
    const group = groups.get(groupKey);
    if (group.wanted !== wanted) continue;
    group.gifts.push(gift);
  }
  const updates = [];
  for (const group of groups.values()) {
    const total = group.gifts.reduce((sum, gift) => sum + nyluraCartQuantity(gift), 0);
    if (total === group.wanted) continue;
    let remaining = group.wanted;
    group.gifts.forEach((gift, index) => {
      const quantity = nyluraCartQuantity(gift);
      const wanted = total < group.wanted && index === 0
        ? quantity + group.wanted - total : Math.min(quantity, remaining);
      remaining -= wanted;
      if (wanted === quantity) return;
      if (wanted === 0 ? gift.instructions?.can_remove === false : gift.instructions?.can_update_quantity === false) return;
      updates.push({ key: gift.key, quantity: wanted });
    });
  }
  return updates;
}
function reconcileNyluraGifts(snapshot) {
  if (!planNyluraGiftUpdates(snapshot).length) return Promise.resolve(snapshot);
  const task = nyluraGiftSyncQueue.then(async () => {
    let cart = await request('cart.js');
    const limit = Math.min(100, Math.max(8, (cart.items?.length || 0) * 2));
    for (let attempt = 0; attempt < limit; attempt++) {
      const update = planNyluraGiftUpdates(cart)[0];
      if (!update) return cart;
      // One exact existing key per write: never send a full-cart replacement or a variant-id update.
      const updated = await request('cart/change.js', { method: 'POST', body: JSON.stringify({ id: update.key, quantity: update.quantity }) });
      const changed = updated.items?.find(item => item.key === update.key);
      if (update.quantity === 0 ? Boolean(changed) : changed && nyluraCartQuantity(changed) !== update.quantity) {
        throw Error('Die Gratiszugabe konnte gerade nicht aktualisiert werden. Bitte versuche es erneut.');
      }
      cart = updated;
    }
    throw Error('Die Gratiszugabe konnte gerade nicht aktualisiert werden. Bitte versuche es erneut.');
  });
  nyluraGiftSyncQueue = task.then(() => undefined, () => undefined);
  return task;
}
/* End Nylura managed gift reconciliation. */

/* Nylura optional cart protection: explicit native checkbox, actual server cart only. */
let cartProtectionPending = false;
let cartProtectionUncertain = false;
const cartProtectionCheckoutHrefs = new WeakMap();
const cartProtectionChoiceKey = 'nylura_shipping_protection';
let cartOperationQueue = Promise.resolve();
let cartProtectionWorkCount = 0;
function queueCartOperation(work) {
  cartProtectionWorkCount++;
  cartProtectionPending = true;
  syncCartProtection();
  const task = cartOperationQueue.then(work);
  cartOperationQueue = task.then(() => undefined, () => undefined);
  return task.finally(() => {
    cartProtectionWorkCount--;
    cartProtectionPending = cartProtectionWorkCount > 0;
    syncCartProtection();
  });
}
function hasCartProtectionCargo(cart) {
  return Array.isArray(cart?.items) && cart.items.some(item => Number(item.quantity) > 0 && !isCartProtection(item) && !isCartGift(item));
}
async function setCartProtectionChoice(cart, choice) {
  if (String(cart.attributes?.[cartProtectionChoiceKey] ?? '') === choice) return cart;
  cartProtectionUncertain = true;
  syncCartProtection();
  // Only the single owned key is submitted; never copy a stale attributes map or send item updates.
  const updated = await request('cart/update.js', {
    method: 'POST', body: JSON.stringify({ attributes: { [cartProtectionChoiceKey]: choice } })
  });
  if (String(updated.attributes?.[cartProtectionChoiceKey] ?? '') !== choice) {
    throw Error('Deine Versandschutz-Auswahl konnte gerade nicht gespeichert werden.');
  }
  return updated;
}
async function removeCartProtectionLines(cart, config) {
  let attempts = 0;
  while (cartProtectionLines(cart, config.variant).length) {
    const line = cartProtectionLines(cart, config.variant)[0];
    if (!line.key || ++attempts > 100) throw Error('Der Versandschutz konnte gerade nicht entfernt werden.');
    cartProtectionUncertain = true;
    syncCartProtection();
    cart = await request('cart/change.js', { method: 'POST', body: JSON.stringify({ id: line.key, quantity: 0 }) });
    if (cart.items.some(item => item.key === line.key)) throw Error('Der Versandschutz konnte gerade nicht entfernt werden.');
  }
  return cart;
}
async function normalizeCartProtectionLines(cart, config) {
  // Protection is a single per-order charge. Resolve the current exact line key
  // after every write because Shopify may change keys when discounts recalculate.
  for (let attempt = 0; attempt < 100; attempt++) {
    const lines = cartProtectionLines(cart, config.variant);
    if (!lines.length) throw Error('Der Versandschutz konnte gerade nicht bestätigt werden.');
    const line = lines.length > 1 ? lines[lines.length - 1] : lines[0];
    const quantity = lines.length > 1 ? 0 : 1;
    if (lines.length === 1 && Number(line.quantity) === 1) return cart;
    if (!line.key) throw Error('Der Versandschutz konnte gerade nicht aktualisiert werden.');
    cartProtectionUncertain = true;
    syncCartProtection();
    const before = lines.map(item => [item.key, Number(item.quantity)]);
    cart = await request('cart/change.js', { method: 'POST', body: JSON.stringify({ id: line.key, quantity }) });
    const after = cartProtectionLines(cart, config.variant).map(item => [item.key, Number(item.quantity)]);
    if (JSON.stringify(before) === JSON.stringify(after)) {
      throw Error('Der Versandschutz konnte gerade nicht aktualisiert werden.');
    }
  }
  throw Error('Der Versandschutz konnte gerade nicht aktualisiert werden.');
}
async function reconcileDefaultCartProtection(cart) {
  const liveOffer = document.querySelector('.mojo-sf[data-offer-id="cmvjpyi5d09liobkpn3zyepcz"]');
  if (liveOffer?.__mojoSettings?.stickyAtcTitle === '{{product}} · ohne Versandschutz') {
    const excluded = [...document.querySelectorAll('[data-cart-protection]')].map(cartProtectionConfig).filter(Boolean);
    for (const config of excluded) cart = await removeCartProtectionLines(cart, config);
    return setCartProtectionChoice(cart, 'off');
  }
  const config = [...document.querySelectorAll('[data-cart-protection]')].map(cartProtectionConfig).find(candidate =>
    candidate?.container.dataset.cartProtectionDefault === 'true');
  if (!config) return cart;
  if (!hasCartProtectionCargo(cart)) {
    // The cart lifecycle ends when its final actual purchase line is removed, even if a paid protection line remains.
    cart = await removeCartProtectionLines(cart, config);
    // PDP choice may be made before the first soap enters the cart.
    if (document.querySelector('[data-cart-protection-prepurchase]')) return cart;
    return setCartProtectionChoice(cart, '');
  }
  if (cart.attributes?.[cartProtectionChoiceKey] === 'off') {
    return removeCartProtectionLines(cart, config);
  }
  if (!config.available && !cartProtectionLines(cart, config.variant).length) return cart;
  cart = await setCartProtectionChoice(cart, 'on');
  if (!cartProtectionLines(cart, config.variant).length) {
    cartProtectionUncertain = true;
    syncCartProtection();
    await request('cart/add.js', { method: 'POST', body: JSON.stringify({ items: [{ id: config.variant, quantity: 1 }] }) });
    cart = await request('cart.js');
  }
  return normalizeCartProtectionLines(cart, config);
}

function cartProtectionConfig(container) {
  if (!container) return null;
  const variant = Number(container.dataset.cartProtectionVariant);
  if (!Number.isSafeInteger(variant) || variant <= 0) return null;
  return { container, variant, available: container.dataset.cartProtectionAvailable === 'true' };
}
function cartProtectionVariantIds() {
  return new Set([...document.querySelectorAll('[data-cart-protection]')].map(cartProtectionConfig).filter(Boolean).map(config => String(config.variant)));
}
function isCartProtection(item) {
  return cartProtectionVariantIds().has(String(item?.variant_id ?? item?.id));
}
function cartProtectionLines(cart, variant) {
  return Array.isArray(cart?.items) ? cart.items.filter(item => String(item?.variant_id ?? item?.id) === String(variant)) : [];
}
function syncCartProtection(cart = cartState) {
  const known = Array.isArray(cart?.items);
  document.querySelectorAll('[data-cart-protection]').forEach(container => {
    const config = cartProtectionConfig(container);
    const input = container.querySelector('[data-cart-protection-toggle]');
    if (!input) return;
    const lines = config && known ? cartProtectionLines(cart, config.variant) : [];
    const prepurchase = container.hasAttribute('data-cart-protection-prepurchase');
    if (known) input.checked = prepurchase && !hasCartProtectionCargo(cart)
      ? cart.attributes?.[cartProtectionChoiceKey] !== 'off'
      : lines.some(item => Number(item.quantity) > 0);
    input.disabled = !config || !known || activeRequest || cartProtectionPending ||
      (!prepurchase && cart.items.length === 0) || (!config.available && !lines.length);
    input.setAttribute('aria-busy', String(cartProtectionPending));
    const price = container.querySelector('[data-cart-protection-price]');
    const unit = Number(container.dataset.cartProtectionUnitPrice);
    if (price && known && config && Number.isFinite(unit) && unit >= 0) {
      const total = lines.reduce((sum, item) => sum + cartCents(item.final_line_price ?? item.line_price,
        cartCents(item.final_price ?? item.price) * Math.max(0, Number(item.quantity) || 0)), 0);
      price.textContent = '+ ' + money(lines.length ? total : unit);
    }
  });
  const blocked = cartProtectionPending || cartProtectionUncertain;
  document.querySelectorAll('[data-cart-checkout]').forEach(link => {
    if (blocked) {
      if (!cartProtectionCheckoutHrefs.has(link)) cartProtectionCheckoutHrefs.set(link, link.getAttribute('href'));
      link.removeAttribute('href');
      link.setAttribute('aria-disabled', 'true');
      link.setAttribute('aria-busy', 'true');
    } else if (cartProtectionCheckoutHrefs.has(link)) {
      const href = cartProtectionCheckoutHrefs.get(link);
      if (href != null) link.setAttribute('href', href);
      cartProtectionCheckoutHrefs.delete(link);
      link.removeAttribute('aria-disabled');
      link.removeAttribute('aria-busy');
    }
  });
}
function setCartRequestActive(value) {
  activeRequest = Boolean(value);
  syncCartProtection();
}
async function toggleCartProtection(input) {
  const config = cartProtectionConfig(input?.closest('[data-cart-protection]'));
  const wanted = Boolean(input?.checked);
  if (!config || activeRequest || cartProtectionPending) { syncCartProtection(); return; }
  const errorEl = document.querySelector('[data-cart-error]');
  if (errorEl) errorEl.hidden = true;
  setCartRequestActive(true);
  try {
    await queueCartOperation(async () => {
      try {
        let cart = await request('cart.js');
        const prepurchase = config.container.hasAttribute('data-cart-protection-prepurchase');
        if (prepurchase && !hasCartProtectionCargo(cart)) {
          cart = await setCartProtectionChoice(cart, wanted ? 'on' : 'off');
          cart = await removeCartProtectionLines(cart, config);
        } else if (wanted && hasCartProtectionCargo(cart)) {
          if (!config.available && !cartProtectionLines(cart, config.variant).length) throw Error('Der Versandschutz ist derzeit nicht verfügbar.');
          cart = await setCartProtectionChoice(cart, 'on');
          if (!cartProtectionLines(cart, config.variant).length) {
            cartProtectionUncertain = true;
            syncCartProtection();
            await request('cart/add.js', { method: 'POST', body: JSON.stringify({ items: [{ id: config.variant, quantity: 1 }] }) });
            cart = await request('cart.js');
          }
        } else if (!wanted) {
          // Persist the explicit opt-out BEFORE removal so following reloads/refreshes cannot reinsert it.
          cart = await setCartProtectionChoice(cart, 'off');
          cart = await removeCartProtectionLines(cart, config);
        }
        cart = await reconcileNyluraGifts(cart);
        cart = await reconcileDefaultCartProtection(cart);
        ++cartRefreshVersion;
        await renderCart(cart);
      } catch (error) {
        try { ++cartRefreshVersion; await renderCart(await request('cart.js')); } catch {}
        throw error;
      }
    });
  } catch (error) {
    if (errorEl) { errorEl.textContent = error.message; errorEl.hidden = false; }
  } finally { setCartRequestActive(false); }
}
document.addEventListener('change', event => {
  if (event.target?.matches?.('input[data-cart-protection-toggle]')) toggleCartProtection(event.target);
});
function guardCartProtectionCheckout(event) {
  if ((cartProtectionPending || cartProtectionUncertain) && event.target?.closest?.('[data-cart-checkout]')) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}
document.addEventListener('click', guardCartProtectionCheckout, true);
document.addEventListener('auxclick', guardCartProtectionCheckout, true);
syncCartProtection();
/* End Nylura optional cart protection. */

function isCartGift(item) {
  const properties = item?.properties || {};
  let bundle = properties.__mojo_bundle;
  if (typeof bundle === 'string') { try { bundle = JSON.parse(bundle); } catch { bundle = null; } }
  return String(properties._nylura_gift) === 'true' || item?.variant_title === 'Gratiszugabe' || bundle?.role === 'gift' ||
    (cartCents(item?.original_line_price, cartCents(item?.original_price ?? item?.price)) === 0 &&
     cartCents(item?.final_line_price ?? item?.line_price, cartCents(item?.final_price ?? item?.price)) === 0);
}
function canChangeCartQuantity(item, quantity) {
  if (isCartProtection(item)) return Number(quantity) === 0;
  return !isCartGift(item) || Number(quantity) === 0 || Number(quantity) === Number(item.quantity);
}
function calculateCartLine(item, compareAt = 0) {
  const quantity = Math.max(1, Math.trunc(Number(item.quantity) || 1));
  const final = cartCents(item.final_line_price ?? item.line_price, cartCents(item.final_price ?? item.price) * quantity);
  const original = cartCents(item.original_line_price, cartCents(item.original_price ?? item.price) * quantity);
  const gift = isCartGift(item);
  const baseline = gift || isCartProtection(item) ? final : Math.max(original, cartCents(compareAt) * quantity, final);
  return {quantity, final, baseline, savings: Math.max(0, baseline - final)};
}
function calculateCartTotals(cart, lines) {
  const baseline = lines.reduce((sum, line) => sum + line.baseline, 0);
  const final = cartCents(cart.total_price, lines.reduce((sum, line) => sum + line.final, 0));
  return {baseline, final, savings: Math.max(0, baseline - final)};
}

const cartTimerStorageKey = 'nylura_cart_timer_expires_v1';
let cartTimerMemoryExpiry = 0;
let cartTimerInterval = 0;
let cartTimerHasItems = false;
function cartTimerExpiry() {
  try { return Number(window.localStorage.getItem(cartTimerStorageKey)) || cartTimerMemoryExpiry; }
  catch { return cartTimerMemoryExpiry; }
}
function updateCartTimer() {
  const expiry = cartTimerExpiry();
  const seconds = Math.max(0, Math.ceil((expiry - Date.now()) / 1000));
  const text = seconds > 0 ? 'Dein Warenkorb wird reserviert: ' + String(Math.floor(seconds / 60)).padStart(2, '0') + ':' + String(seconds % 60).padStart(2, '0') : 'Dein Warenkorb bleibt gespeichert';
  document.querySelectorAll('[data-cart-timer]').forEach(element => { element.hidden = !cartTimerHasItems || !expiry; element.textContent = text; });
  if ((!cartTimerHasItems || seconds === 0) && cartTimerInterval) { clearInterval(cartTimerInterval); cartTimerInterval = 0; }
}
function startCartTimer(cart) {
  cartTimerHasItems = Boolean(cart.items?.length);
  if (Array.isArray(cart.items) && cart.items.length === 0) {
    cartTimerMemoryExpiry = 0;
    try { window.localStorage.removeItem(cartTimerStorageKey); } catch {}
  }
  if (cartTimerHasItems && !cartTimerExpiry()) {
    cartTimerMemoryExpiry = Date.now() + 10 * 60 * 1000;
    try { window.localStorage.setItem(cartTimerStorageKey, String(cartTimerMemoryExpiry)); } catch {}
  }
  updateCartTimer();
  if (cartTimerHasItems && cartTimerExpiry() > Date.now() && !cartTimerInterval) cartTimerInterval = setInterval(updateCartTimer, 1000);
}

const cartProductCache = new Map();
let cartRenderVersion = 0;
let cartRefreshVersion = 0;
function cartFallbackComparisons() {
  try { return JSON.parse(document.querySelector('[data-cart-compare-prices]')?.textContent || '{}'); }
  catch { return {}; }
}
async function cartCompareAt(item, fallback) {
  if (isCartGift(item)) return 0;
  const handle = item.handle || item.url?.match(/\/products\/([^/?#]+)/)?.[1];
  const variantId = item.variant_id || item.id;
  if (handle) {
    if (!cartProductCache.has(handle)) cartProductCache.set(handle, request('products/' + encodeURIComponent(handle) + '.js').catch(() => null));
    const product = await cartProductCache.get(handle);
    const variant = product?.variants?.find(candidate => String(candidate.id) === String(variantId));
    if (variant?.compare_at_price != null) return cartCents(variant.compare_at_price);
  }
  return cartCents(fallback[String(variantId)]);
}
async function renderCart(cart) {
  const version = ++cartRenderVersion;
  const fallback = cartFallbackComparisons();
  const comparisons = await Promise.all(cart.items.map(item => cartCompareAt(item, fallback)));
  if (version !== cartRenderVersion) return;
  const lines = cart.items.map((item, index) => calculateCartLine(item, comparisons[index]));
  const totals = calculateCartTotals(cart, lines);
  cartState = cart;
  startCartTimer(cart);
  document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = cart.item_count; el.hidden = el.classList.contains('cart-count-bubble') && !cart.item_count; });
  document.querySelectorAll('[data-cart-total],[data-cart-checkout-total]').forEach(el => el.textContent = money(totals.final));
  document.querySelectorAll('[data-cart-checkout-compare]').forEach(el => { el.textContent = money(totals.baseline); el.hidden = totals.baseline <= totals.final; });
  document.querySelectorAll('[data-cart-savings]').forEach(el => el.textContent = '−' + money(totals.savings));
  document.querySelectorAll('[data-cart-savings-row]').forEach(el => el.hidden = totals.savings <= 0);
  const renderedItems = cart.items.map((item, index) => {
    const prices = lines[index];
    return `<article class="nylura-cart-item"><img src="${escapeHTML(item.image || '')}" alt="${escapeHTML(item.product_title)}"><div><h3><a href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a></h3><p>${item.variant_title === 'Default Title' ? '' : escapeHTML(item.variant_title)}</p><div class="nylura-cart-item__quantity">${isCartProtection(item) ? `<span aria-label="Versandschutz pro Bestellung">${item.quantity}</span>` : isCartGift(item) ? `<span aria-label="Geschenkmenge">${item.quantity}</span>` : `<button type="button" data-cart-key="${escapeHTML(item.key)}" data-cart-quantity="${item.quantity - 1}" aria-label="Menge verringern">−</button><span>${item.quantity}</span><button type="button" data-cart-key="${escapeHTML(item.key)}" data-cart-quantity="${item.quantity + 1}" aria-label="Menge erhöhen">+</button>`}</div><button class="nylura-cart-item__remove" type="button" data-cart-key="${escapeHTML(item.key)}" data-cart-quantity="0">Entfernen</button></div><span class="nylura-cart-item__price">${prices.baseline > prices.final ? `<s aria-label="Regulärer Gesamtpreis">${money(prices.baseline)}</s>` : ''}<strong>${money(prices.final)}</strong></span></article>`;
  });
  const emptyHTML = '<div class="nylura-cart-empty"><h3>Dein Warenkorb ist leer.</h3><p>Entdecke Dein neues Pflegeritual.</p></div>';
  document.querySelectorAll('[data-cart-items],[data-cart-page-items]').forEach(el => {
    el.innerHTML = cart.items.length ? renderedItems.filter((html, index) =>
      !el.matches('[data-cart-items]') || !isCartProtection(cart.items[index])).join('') : emptyHTML;
  });
  cartProtectionUncertain = false;
  syncCartProtection(cart);
}
function refreshCart() {
  const version = ++cartRefreshVersion;
  return queueCartOperation(async () => {
    let cart;
    try {
      cart = await request('cart.js');
      cart = await reconcileNyluraGifts(cart);
      cart = await reconcileDefaultCartProtection(cart);
    } catch (error) {
      try { cart = await request('cart.js'); if (version === cartRefreshVersion) await renderCart(cart); } catch {}
      throw error;
    }
    if (version === cartRefreshVersion) await renderCart(cart);
    return cart;
  });
}

function selectedProductItem(scope=document){const picker=scope.querySelector('[data-bundle-picker]');if(picker){const card=picker.querySelector('.is-selected');if(!card?.dataset.bundleVariant)throw Error('Dieses Angebot ist derzeit nicht verfügbar.');return {item:{id:Number(card.dataset.bundleVariant),quantity:1},bars:Number(card.dataset.bundleQuantity),gift:Number(picker.dataset.giftVariant)||null};}return{item:{id:Number(scope.querySelector('[data-native-variant]')?.value),quantity:Math.max(1,Number(scope.querySelector('[data-native-quantity]')?.value)||1)},bars:0,gift:null};}
function proceedToNyluraCheckout(cart) {
  if (cartProtectionPending || cartProtectionUncertain) throw Error('Bitte warte, bis Dein Warenkorb vollständig aktualisiert ist.');
  if (!Array.isArray(cart?.items) || !cart.items.some(item =>
      item.handle === 'deodorizing-persimmon-soap' && Number(item.quantity) > 0 && !isCartGift(item))) {
    throw Error('Bitte wähle zuerst Dein Seifen-Angebot aus.');
  }
  window.location.assign(endpoint('checkout'));
}
async function addItems(items){await queueCartOperation(()=>request('cart/add.js',{method:'POST',body:JSON.stringify({items})}));const cart=await refreshCart();proceedToNyluraCheckout(cart);}
async function submitProduct(trigger){if(activeRequest||cartProtectionPending)return;const scope=trigger?.closest('[data-nylura-product-scope]')||document;const errors=scope.querySelector('[data-product-error]');if(errors)errors.hidden=true;const buttons=[...document.querySelectorAll('[data-submit-product]')];try{setCartRequestActive(true);buttons.forEach(b=>b.disabled=true);const selection=selectedProductItem(scope);const current=await request('cart.js');const items=[selection.item];if(selection.bars>=6&&selection.gift&&!current.items.some(i=>i.variant_id===selection.gift))items.push({id:selection.gift,quantity:1,properties:{_nylura_gift:'true'}});await addItems(items);}catch(error){if(errors){errors.textContent=error.message;errors.hidden=false;}}finally{setCartRequestActive(false);buttons.forEach(b=>b.disabled=false);}}

function syncBundlePrice(picker){
const card=picker.querySelector('[data-bundle-variant].is-selected');if(!card)return;
picker.querySelectorAll('[data-bundle-variant]').forEach(option=>{const unit=option.querySelector('[data-bundle-unit-price]');if(unit)unit.textContent=money(Math.round(Number(option.dataset.bundlePrice)/Number(option.dataset.bundleQuantity)));});
const scope=picker.closest('[data-nylura-product-scope]')||document;const price=scope.querySelector('[data-sticky-price]')||document.querySelector('[data-sticky-price]');if(price)price.textContent=money(Number(card.dataset.bundlePrice));
}
function updateBundle(card){const picker=card.closest('[data-bundle-picker]');picker.querySelectorAll('[data-bundle-variant]').forEach(el=>{const active=el===card;el.classList.toggle('is-selected',active);el.setAttribute('aria-checked',String(active));});const gift=picker.querySelector('[data-bundle-gift]');if(gift)gift.hidden=Number(card.dataset.bundleQuantity)<6;syncBundlePrice(picker);}
async function cartChange(key, quantity) {
  if (activeRequest || cartProtectionPending) return;
  const existingItem = cartState?.items?.find(item => item.key === key);
  if (existingItem && !canChangeCartQuantity(existingItem, quantity)) return;
  const errorEl = document.querySelector('[data-cart-error]');
  if (errorEl) errorEl.hidden = true;
  setCartRequestActive(true);
  try {
    await queueCartOperation(async () => {
      try {
        // Removing protection from the regular cart page is also an explicit opt-out.
        if (existingItem && isCartProtection(existingItem) && Number(quantity) === 0) {
          await setCartProtectionChoice(await request('cart.js'), 'off');
        }
        let cart = await request('cart/change.js', { method: 'POST', body: JSON.stringify({ id: key, quantity }) });
        cart = await reconcileNyluraGifts(cart);
        cart = await reconcileDefaultCartProtection(cart);
        ++cartRefreshVersion;
        await renderCart(cart);
      } catch (error) {
        try { ++cartRefreshVersion; await renderCart(await request('cart.js')); } catch {}
        throw error;
      }
    });
  } catch (error) {
    if (errorEl) { errorEl.textContent = error.message; errorEl.hidden = false; }
  } finally { setCartRequestActive(false); }
}

/* Nylura scoped FAQ accordion: matches the paired ARIA/hidden Liquid markup. */
function setNyluraFaqExpanded(item, expanded) {
  const header = item.querySelector('.faq-item__header');
  const answer = item.querySelector('.faq-item__answer');
  if (!header || !answer) return;
  item.classList.toggle('active', expanded);
  header.setAttribute('aria-expanded', String(expanded));
  answer.hidden = !expanded;
}
function toggleNyluraFaq(header) {
  const section = header?.closest('.faq-section[data-nylura-faq]');
  const item = header?.closest('.faq-item');
  if (!section || !item || item.closest('.faq-section') !== section) return;
  const expanded = !item.classList.contains('active');
  section.querySelectorAll('.faq-item').forEach(candidate => {
    if (candidate.closest('.faq-section') === section) setNyluraFaqExpanded(candidate, candidate === item && expanded);
  });
}
function initializeNyluraFaqs(scope = document) {
  scope.querySelectorAll('.faq-section[data-nylura-faq]').forEach(section => {
    let activeFound = false;
    section.querySelectorAll('.faq-item').forEach(item => {
      if (item.closest('.faq-section') !== section) return;
      const expanded = !activeFound && item.classList.contains('active');
      activeFound ||= expanded;
      setNyluraFaqExpanded(item, expanded);
    });
  });
}
document.addEventListener('click', event => {
  const header = event.target?.closest?.('.faq-section[data-nylura-faq] .faq-item__header');
  if (!header) return;
  event.preventDefault();
  toggleNyluraFaq(header);
});
document.addEventListener('shopify:section:load', event => initializeNyluraFaqs(event.target));
initializeNyluraFaqs();
/* End Nylura scoped FAQ accordion. */

document.addEventListener('click',event=>{const target=event.target.closest('button,a,[data-cart-overlay]');if(!target)return;if(target.matches('[data-trigger-mojo-form]')){event.preventDefault();const form=document.getElementById(target.dataset.triggerMojoForm);const submitter=form?.querySelector('[type="submit"]');if(submitter)submitter.click();else form?.requestSubmit();return;}if(target.matches('[data-cart-open]')){event.preventDefault();cartOpen();refreshCart().catch(error=>{const el=document.querySelector('[data-cart-error]');el.textContent=error.message;el.hidden=false;});return;}if(target.matches('[data-cart-close],[data-cart-overlay]')){cartClose();return;}if(target.matches('[data-submit-product]')){event.preventDefault();submitProduct(target);return;}if(target.matches('[data-bundle-variant]')){updateBundle(target);return;}if(target.matches('[data-add-variant]')){event.preventDefault();if(activeRequest||cartProtectionPending)return;setCartRequestActive(true);target.disabled=true;addItems([{id:Number(target.dataset.addVariant),quantity:1}]).catch(error=>{const el=document.querySelector('[data-cart-error]');el.textContent=error.message;el.hidden=false;cartOpen();}).finally(()=>{target.disabled=false;setCartRequestActive(false);});return;}if(target.matches('[data-cart-key]')){cartChange(target.dataset.cartKey,Number(target.dataset.cartQuantity));return;}if(target.matches('[data-gallery-index]')){const gallery=target.closest('[data-gallery]');gallery.querySelectorAll('[data-gallery-slide]').forEach(el=>el.classList.toggle('is-active',el.dataset.gallerySlide===target.dataset.galleryIndex));gallery.querySelectorAll('[data-gallery-index]').forEach(el=>el.classList.toggle('is-active',el===target));return;}if(target.matches('[data-gallery-slide]')){const modal=document.querySelector('[data-image-modal]');modal.querySelector('img').src=target.querySelector('img').src;modal.showModal();return;}if(target.matches('[data-image-modal-close]')){document.querySelector('[data-image-modal]').close();return;}
const emblaArrow=event.target.closest('.embla__button--prev,.embla__button--next');if(emblaArrow){const viewport=emblaArrow.closest('.embla')?.querySelector('.embla__viewport');if(viewport)viewport.scrollBy({left:(emblaArrow.classList.contains('embla__button--next')?1:-1)*viewport.clientWidth,behavior:'smooth'});return;}
const arrow=event.target.closest('.slider-button, .splide__arrow');if(arrow){const component=arrow.closest('slider-component,splide-component');const track=component?.querySelector('.slider,.splide__track');if(track){const next=arrow.name==='next'||arrow.classList.contains('splide__arrow--next');track.scrollBy({left:(next?1:-1)*track.clientWidth,behavior:'smooth'});}}
});
document.addEventListener('click',event=>{const track=event.target.closest('[data-track-order]');if(track){const input=document.getElementById('YQNum');const number=input?.value.trim();if(!number){input?.focus();return;}const result=document.querySelector('[data-track-result]');const link=document.createElement('a');link.href='https://t.17track.net/de#nums='+encodeURIComponent(number);link.target='_blank';link.rel='noopener';link.textContent='Sendung bei 17TRACK verfolgen';link.className='button';result.replaceChildren(link);link.click();}});
document.addEventListener('keydown',event=>{if(event.key==='Escape')cartClose();const drawer=document.querySelector('[data-cart-drawer]');if(event.key==='Tab'&&drawer.classList.contains('is-open')){const focusables=[...drawer.querySelectorAll('button,a,input,select')].filter(el=>!el.disabled&&el.offsetParent!==null);const first=focusables[0],last=focusables.at(-1);if(event.shiftKey&&document.activeElement===first){event.preventDefault();last.focus();}else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first.focus();}}});
document.querySelectorAll('video').forEach(video=>{if(video.dataset.autoplay==='true'||video.hasAttribute('autoplay')||video.closest('internal-video')?.dataset.autoplay==='true'){video.muted=true;video.play().catch(()=>{});}});
document.querySelectorAll('.embla__viewport').forEach(el=>{el.style.overflowX='auto';el.style.scrollSnapType='x mandatory';});
document.querySelectorAll('.splide__track').forEach(el=>{el.style.overflowX='auto';el.style.scrollSnapType='x mandatory';});
document.querySelectorAll('[data-bundle-picker]').forEach(syncBundlePrice);
const submit=document.querySelector('.product-form__submit');if(submit&&'IntersectionObserver'in window){new IntersectionObserver(entries=>document.querySelector('[data-sticky-atc]')?.classList.toggle('is-visible',!entries[0].isIntersecting&&entries[0].boundingClientRect.top<0),{threshold:0}).observe(submit);}

document.addEventListener('submit',event=>{const form=event.target;if(!form.matches('[data-nylura-cart-add]'))return;event.preventDefault();if(activeRequest||cartProtectionPending)return;const fields=new FormData(form);const id=Number(fields.get('id'));if(!id)return;setCartRequestActive(true);const button=form.querySelector('[type="submit"]');if(button)button.disabled=true;addItems([{id,quantity:Number(fields.get('quantity'))||1}]).catch(error=>{const el=document.querySelector('[data-cart-error]');el.textContent=error.message;el.hidden=false;cartOpen();}).finally(()=>{setCartRequestActive(false);if(button)button.disabled=false;});});
const params=new URLSearchParams(location.search);document.querySelectorAll('#FacetFiltersForm input,#FacetFiltersFormMobile input').forEach(input=>{if(input.type==='checkbox')input.checked=params.getAll(input.name).includes(input.value);else if(params.has(input.name))input.value=params.get(input.name);});
function submitFacets(form){if(!form)return;form.querySelectorAll('input[type="number"]').forEach(input=>{if(input.value==='')input.disabled=true;});form.requestSubmit();}
document.addEventListener('change',event=>{const form=event.target.closest('#FacetFiltersForm,#FacetFiltersFormMobile');if(form&&form.id==='FacetFiltersForm')submitFacets(form);});
document.addEventListener('click',event=>{const apply=event.target.closest('.mobile-facets__footer .button');if(apply){submitFacets(apply.closest('form'));return;}const close=event.target.closest('.mobile-facets__close');if(close){const details=close.closest('menu-drawer')?.querySelector('details');if(details){details.open=false;details.classList.remove('menu-opening');}}});
document.querySelectorAll('menu-drawer>details').forEach(details=>details.addEventListener('toggle',()=>details.classList.toggle('menu-opening',details.open)));
document.querySelectorAll('price-range').forEach(range=>{const max=Number(range.dataset.max)||0;const low=range.querySelector('[name="filter.v.price.gte"]'),high=range.querySelector('[name="filter.v.price.lte"]');const slider=range.querySelector('.price-slider');if(!low||!high||!slider||!max)return;let minimum=Number(low.value)||0,maximum=Number(high.value)||max;function draw(){const minPercent=minimum/max*100,maxPercent=maximum/max*100;range.querySelector('[data-handle="min"]').style.left=minPercent+'%';range.querySelector('[data-handle="max"]').style.left=maxPercent+'%';const fill=range.querySelector('.price-slider__range');fill.style.left=minPercent+'%';fill.style.width=(maxPercent-minPercent)+'%';range.querySelector('[data-label="min"]').textContent=minimum.toFixed(2);range.querySelector('[data-label="max"]').textContent=maximum.toFixed(2);}draw();range.querySelectorAll('[data-handle]').forEach(handle=>{handle.setAttribute('role','slider');handle.setAttribute('aria-valuemin','0');handle.setAttribute('aria-valuemax',String(max));handle.addEventListener('pointerdown',event=>{event.preventDefault();handle.setPointerCapture(event.pointerId);const drag=move=>{const rect=slider.getBoundingClientRect();const value=Math.round(Math.max(0,Math.min(max,(move.clientX-rect.left)/rect.width*max))*100)/100;if(handle.dataset.handle==='min'){minimum=Math.min(value,maximum);low.value=minimum;}else{maximum=Math.max(value,minimum);high.value=maximum;}handle.setAttribute('aria-valuenow',String(value));draw();};const stop=()=>{handle.removeEventListener('pointermove',drag);handle.removeEventListener('pointerup',stop);if(range.closest('form').id==='FacetFiltersForm')submitFacets(range.closest('form'));};handle.addEventListener('pointermove',drag);handle.addEventListener('pointerup',stop);});handle.addEventListener('keydown',event=>{if(!['ArrowLeft','ArrowRight','Home','End'].includes(event.key))return;event.preventDefault();const step=max/100;let value=handle.dataset.handle==='min'?minimum:maximum;value=event.key==='Home'?0:event.key==='End'?max:value+(event.key==='ArrowRight'?step:-step);if(handle.dataset.handle==='min'){minimum=Math.max(0,Math.min(maximum,value));low.value=minimum;}else{maximum=Math.min(max,Math.max(minimum,value));high.value=maximum;}draw();});});});
document.getElementById('YQNum')?.addEventListener('keydown',event=>{if(event.key==='Enter'){event.preventDefault();document.querySelector('[data-track-order]')?.click();}});

// Explicit bridge for the actual Mojo cart event; no request interception or redispatch.
window.NyluraCart = Object.assign(window.NyluraCart || {}, {refresh: refreshCart, open: cartOpen, close: cartClose});
let mojoCartRefreshTask = null;
let mojoCartRefreshDirty = false;
document.addEventListener('cart:refresh', event => {
  if (event.detail?.source !== 'mojo' && event.detail?.sourceId !== 'mojo') return;
  mojoCartRefreshDirty = true;
  if (mojoCartRefreshTask) return;
  mojoCartRefreshTask = (async () => {
    do {
      mojoCartRefreshDirty = false;
      await window.NyluraCart.refresh();
    } while (mojoCartRefreshDirty);
    proceedToNyluraCheckout(cartState);
  })().catch(error => {
    window.NyluraCart.open();
    const element = document.querySelector('[data-cart-error]');
    if (element) { element.textContent = error.message; element.hidden = false; }
  }).finally(() => { mojoCartRefreshTask = null; });
});

refreshCart().catch(()=>{});
})();

/* NYLURA_MOBILE_GALLERY_START */
;(() => {
  'use strict';
  const mobile = window.matchMedia('(max-width: 749px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const controllers = new WeakMap();

  function initialize(gallery) {
    if (controllers.has(gallery)) return;
    const stage = gallery.querySelector('.nylura-gallery__stage');
    const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
    const thumbs = [...gallery.querySelectorAll('[data-gallery-index]')];
    const thumbStrip = gallery.querySelector('.nylura-gallery__thumbs');
    if (!stage || !slides.length) return;
    let selected = Math.max(0, slides.findIndex(slide => slide.classList.contains('is-active')));
    let scrollFrame = 0;
    let resizeFrame = 0;
    let gesture = null;
    let suppressClickUntil = 0;
    let lastWidth = stage.clientWidth;
    gallery.classList.add('nylura-gallery--swipe');
    stage.setAttribute('role', 'region');
    stage.setAttribute('aria-label', 'Produktbilder');
    stage.setAttribute('aria-roledescription', 'Karussell');

    function revealThumb(index) {
      const thumb = thumbs.find(item => Number(item.dataset.galleryIndex) === index);
      if (!thumb || !thumbStrip) return;
      const bounds = thumbStrip.getBoundingClientRect();
      const rect = thumb.getBoundingClientRect();
      if (rect.left < bounds.left || rect.right > bounds.right) {
        thumbStrip.scrollTo({left: Math.max(0, thumbStrip.scrollLeft + rect.left - bounds.left - (thumbStrip.clientWidth - rect.width) / 2), behavior: 'auto'});
      }
    }
    function setSelected(index, reveal = true) {
      selected = Math.max(0, Math.min(slides.length - 1, index));
      slides.forEach((slide, position) => {
        slide.classList.toggle('is-active', position === selected);
        slide.tabIndex = position === selected ? 0 : -1;
      });
      thumbs.forEach(thumb => {
        const active = Number(thumb.dataset.galleryIndex) === selected;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', String(active));
      });
      if (reveal) revealThumb(selected);
    }
    function align(behavior = 'auto') {
      const left = mobile.matches ? slides[selected].offsetLeft - slides[0].offsetLeft : 0;
      stage.scrollTo({left, behavior});
    }
    function onScroll() {
      if (!mobile.matches || scrollFrame) return;
      scrollFrame = requestAnimationFrame(() => {
        scrollFrame = 0;
        let nearest = 0;
        let distance = Infinity;
        slides.forEach((slide, index) => {
          const value = Math.abs(slide.offsetLeft - slides[0].offsetLeft - stage.scrollLeft);
          if (value < distance) { distance = value; nearest = index; }
        });
        setSelected(nearest);
      });
    }
    function realign() {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = 0;
        setSelected(selected, false);
        align();
      });
    }
    function onClick(event) {
      const thumb = event.target.closest('[data-gallery-index]');
      if (thumb && gallery.contains(thumb)) {
        event.preventDefault();
        // Own thumbnail selection before the historical document-level handler.
        event.stopImmediatePropagation();
        setSelected(Number(thumb.dataset.galleryIndex));
        align(reducedMotion.matches ? 'auto' : 'smooth');
        return;
      }
      if (event.target.closest('[data-gallery-slide]') && event.detail !== 0 && Date.now() < suppressClickUntil) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
      // A normal image tap reaches the existing lightbox handler unchanged.
    }
    function onKeydown(event) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? slides.length - 1 : selected + (event.key === 'ArrowRight' ? 1 : -1);
      setSelected(next);
      slides[selected].focus({preventScroll: true});
      align(reducedMotion.matches ? 'auto' : 'smooth');
    }
    function onPointerDown(event) {
      gesture = {x: event.clientX, y: event.clientY, moved: false};
    }
    function onPointerMove(event) {
      if (gesture && (Math.abs(event.clientX - gesture.x) > 10 || Math.abs(event.clientY - gesture.y) > 10)) gesture.moved = true;
    }
    function finishGesture() {
      if (gesture?.moved) suppressClickUntil = Date.now() + 500;
      gesture = null;
    }
    gallery.addEventListener('click', onClick, true);
    stage.addEventListener('scroll', onScroll, {passive: true});
    stage.addEventListener('keydown', onKeydown);
    stage.addEventListener('pointerdown', onPointerDown, {passive: true});
    stage.addEventListener('pointermove', onPointerMove, {passive: true});
    stage.addEventListener('pointerup', finishGesture, {passive: true});
    stage.addEventListener('pointercancel', finishGesture, {passive: true});
    const observer = 'ResizeObserver' in window ? new ResizeObserver(() => {
      if (stage.clientWidth !== lastWidth) { lastWidth = stage.clientWidth; realign(); }
    }) : null;
    observer?.observe(stage);
    controllers.set(gallery, {realign, destroy() {
      observer?.disconnect();
      cancelAnimationFrame(scrollFrame);
      cancelAnimationFrame(resizeFrame);
      gallery.removeEventListener('click', onClick, true);
      stage.removeEventListener('scroll', onScroll);
      stage.removeEventListener('keydown', onKeydown);
      stage.removeEventListener('pointerdown', onPointerDown);
      stage.removeEventListener('pointermove', onPointerMove);
      stage.removeEventListener('pointerup', finishGesture);
      stage.removeEventListener('pointercancel', finishGesture);
      controllers.delete(gallery);
    }});
    setSelected(selected, false);
    realign();
  }
  function initializeAll(scope = document) { scope.querySelectorAll('[data-gallery]').forEach(initialize); }
  function realignAll() { document.querySelectorAll('[data-gallery]').forEach(gallery => controllers.get(gallery)?.realign()); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeAll(), {once: true});
  else initializeAll();
  if (mobile.addEventListener) mobile.addEventListener('change', realignAll);
  else mobile.addListener(realignAll);
  window.addEventListener('resize', realignAll, {passive: true});
  document.addEventListener('shopify:section:load', event => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-gallery]').forEach(gallery => controllers.get(gallery)?.destroy()));
})();
/* NYLURA_MOBILE_GALLERY_END */

/* NYLURA_MOJO_DOM_INIT_START */
/* Nylura's original four native package cards: read public inline-offer and variant data.
   Correct unselected display only; Mojo owns selection, form, active price, gifts and cart. */
(() => {
  'use strict';
  const observed = new WeakSet();
  const packKeys = new Map([['nylura-pack-2', 2], ['nylura-pack-3', 3], ['nylura-pack-6', 6], ['nylura-pack-10', 10]]);
  const packVariantIds = new Map([['nylura-pack-2', '50660582686937'], ['nylura-pack-3', '50660582719705'], ['nylura-pack-6', '50660582752473'], ['nylura-pack-10', '50660582785241']]);
  let scheduled = false;

  function parseJSON(raw, fallback = null) {
    try { return JSON.parse(raw); } catch { return fallback; }
  }
  function normalizedId(value) {
    return String(value == null ? '' : value).replace(/^gid:\/\/shopify\/ProductVariant\//, '');
  }
  function selectedTier(root) {
    return root.querySelector('[data-mojo-tier].is-selected') ||
      root.querySelector('[data-mojo-tier].mojo-sf-tier--selected') ||
      root.querySelector('[data-mojo-tier][data-tier-index="' + (root.getAttribute('data-default-index') || '0') + '"]');
  }
  function publicOriginalOffer(scope, root, tiers) {
    if (tiers.length !== 4 || new Set(tiers.map(tier => tier.getAttribute('data-tier-key'))).size !== 4) return null;
    if (tiers.some(tier => !packKeys.has(tier.getAttribute('data-tier-key')) || tier.getAttribute('data-tier-qty') !== '1')) return null;
    const offerId = root.getAttribute('data-offer-id');
    for (const script of scope.querySelectorAll('script[data-mojo-inline]')) {
      const offer = parseJSON(script.textContent);
      if (!offer || offer.productHandle !== 'deodorizing-persimmon-soap' || !Array.isArray(offer.tiers) || offer.tiers.length !== 4) continue;
      if (offerId && offerId !== 'null' && offer.id !== offerId) continue;
      // A matching explicit offer with different economics must never use the
      // historical control display fallback.
      if (offer.settings?.subAttachEnabled || offer.settings?.pricePerItem || offer.settings?.showQtySelector) return null;
      const configs = new Map(offer.tiers.map(config => [config.key, config]));
      if (configs.size !== 4) return null;
      if (tiers.some(tier => {
        const config = configs.get(tier.getAttribute('data-tier-key'));
        return !config || Number(config.quantity) !== 1 || config.discountType !== 'percent_off' || Number(config.discountValue) !== 0 || config.sellingPlanGid || config.showQtySelector;
      })) return null;
      return configs;
    }
    // Mojo can auto-look up the old live control while the draft inline offer
    // has a different id. Public product identity + all four original keys and
    // exact titles identify that control without consulting pending selects.
    if (String(root.getAttribute('data-product-id') || '').replace(/^gid:\/\/shopify\/Product\//, '') !== '10337643036889') return null;
    if (tiers.some(tier => tier.querySelector('.mojo-sf-tier-title')?.textContent.trim() !== packKeys.get(tier.getAttribute('data-tier-key')) + ' Seifenstücke')) return null;
    return new Map(tiers.map(tier => {
      const key = tier.getAttribute('data-tier-key');
      return [key, {defaultVariantIds: [packVariantIds.get(key)]}];
    }));
  }
  function resolveNativePack(tier, config, variants, options) {
    const key = tier.getAttribute('data-tier-key');
    const expectedId = packVariantIds.get(key);
    const defaults = Array.isArray(config.defaultVariantIds) ? config.defaultVariantIds : parseJSON(config.defaultVariantIds, []);
    if (!expectedId || !Array.isArray(defaults) || defaults.length !== 1 || normalizedId(defaults[0]) !== expectedId) return null;
    // These are proven complete-package variants, not quantity-discount tiers.
    // Their saved public default id owns the inactive card price; hidden Mojo
    // selects can still show the base single variant during the first render.
    const variant = variants.find(candidate => normalizedId(candidate.id) === expectedId);
    if (!variant || variant.title !== packKeys.get(key) + ' Seifenstücke' || !Number.isSafeInteger(variant.price) || variant.price < 0) return null;
    return variant;
  }
  function renderUnselectedPrices(scope, root) {
    const tiers = Array.from(root.querySelectorAll('[data-mojo-tier]'));
    const configs = publicOriginalOffer(scope, root, tiers);
    const variants = parseJSON(root.getAttribute('data-product-variants'), []);
    const options = parseJSON(root.getAttribute('data-product-options'), []);
    const currency = root.getAttribute('data-currency');
    if (!configs || !Array.isArray(variants) || !Array.isArray(options) || !/^[A-Z]{3}$/.test(currency || '')) return;
    let formatter;
    try { formatter = new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2}); }
    catch { return; }
    const active = selectedTier(root);
    for (const tier of tiers) {
      if (tier === active) continue;
      const variant = resolveNativePack(tier, configs.get(tier.getAttribute('data-tier-key')), variants, options);
      const current = tier.querySelector('.mojo-sf-tier-price-current');
      if (!variant || !current) continue;
      const text = formatter.format(variant.price / 100);
      if (current.textContent !== text) current.textContent = text;
      const compare = tier.querySelector('.mojo-sf-tier-price-compare');
      if (compare) {
        const compareText = Number.isSafeInteger(variant.compare_at_price) && variant.compare_at_price > variant.price ? formatter.format(variant.compare_at_price / 100) : '';
        if (compare.textContent !== compareText) compare.textContent = compareText;
      }
    }
  }
  function syncStickyPrice(form, root) {
    const current = selectedTier(root)?.querySelector('.mojo-sf-tier-price-current');
    const stickyPrice = form.closest('section')?.querySelector('[data-sticky-atc] [data-sticky-price]');
    if (current?.textContent.trim() && stickyPrice && stickyPrice.textContent !== current.textContent.trim()) stickyPrice.textContent = current.textContent.trim();
  }
  function initialize() {
    scheduled = false;
    document.querySelectorAll('form[data-nylura-mojo-form]').forEach(form => {
      const scope = form.closest('[data-nylura-product-scope]') || form.parentElement;
      if (!scope) return;
      if (!observed.has(scope)) {
        observed.add(scope);
        new MutationObserver(schedule).observe(scope, {childList: true, subtree: true});
        scope.addEventListener('click', schedule);
        scope.addEventListener('change', schedule);
      }
      scope.querySelectorAll('.mojo-sf').forEach(root => {
        if (!root.querySelector('[data-mojo-tier]')) return;
        renderUnselectedPrices(scope, root);
        const group = root.querySelector('.mojo-sf-tiers[role="radiogroup"]');
        if (group && group.getAttribute('aria-label') !== 'Vorrat auswählen') group.setAttribute('aria-label', 'Vorrat auswählen');
        syncStickyPrice(form, root);
      });
    });
  }
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(initialize);
  }
  function start() {
    new MutationObserver(schedule).observe(document.body, {childList: true, subtree: true});
    schedule();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
})();

/* NYLURA_MOJO_DOM_INIT_END */

/* NYLURA_MOJO_V7_20261017_BEGIN */
/* Nylura V7: presentation-only text normalization for one native Mojo parent.
 * Reads/writes visible text nodes only. Never reads/writes offer config, forms,
 * money attributes, prices in requests, cart state, Shopify APIs or checkout.
 */
(function () {
  'use strict';
  if (window.__nyluraV7TextNormalizer) return;
  window.__nyluraV7TextNormalizer = true;
  var selector = '.mojo-sf[data-offer-id="cmvcipqj20avtobkqwbawil4o"]';
  var root = null;
  var textObserver = null;
  var queued = false;
  function normalize() {
    queued = false;
    if (!root || !root.isConnected || !root.matches(selector)) return;
    root.querySelectorAll('.mojo-sf-tier-price-current').forEach(function (price) {
      Array.prototype.forEach.call(price.childNodes, function (node) {
        if (node.nodeType !== 3) return;
        var before = node.nodeValue;
        // NBSP/narrow NBSP are valid locale spaces; 15,50 stays 15,50.
        var after = before.replace(/(\d),00(?=[\s\u00a0\u202f]*€)/g, '$1');
        if (after !== before) node.nodeValue = after;
      });
    });
    root.querySelectorAll('.mojo-sf-tier-price-compare').forEach(function (compare) {
      if (compare.textContent !== '19,95 €') compare.textContent = '19,95 €';
    });
  }
  function schedule() {
    if (queued) return;
    queued = true;
    Promise.resolve().then(normalize);
  }
  function attach() {
    var next = document.querySelector(selector);
    if (next === root && root && root.isConnected) return;
    if (textObserver) textObserver.disconnect();
    root = next;
    if (!root) return;
    normalize();
    textObserver = new MutationObserver(schedule);
    textObserver.observe(root, { childList: true, characterData: true, subtree: true });
  }
  function start() {
    attach();
    // Finds asynchronous Mojo roots or a theme-editor replacement. No writes here.
    new MutationObserver(function () {
      if (!root || !root.isConnected || !root.matches(selector)) attach();
    }).observe(document.documentElement, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['data-offer-id']
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
}());

/* NYLURA_MOJO_V7_20261017_END */

/* Nylura seven offers: 22.10.2026; scoped to cmvjpyi5d09liobkpn3zyepcz */
/* Only show rewards that can actually be unlocked within this offer. */
window.NyluraSevenBenefits = (() => {
  const truck='<svg viewBox="0 0 64 48" fill="none" aria-hidden="true"><path d="M5 8h34v26H5zM39 18h11l9 10v6H39z" fill="currentColor"/><circle cx="17" cy="36" r="6" fill="white" stroke="currentColor" stroke-width="4"/><circle cx="48" cy="36" r="6" fill="white" stroke="currentColor" stroke-width="4"/><path d="M43 21h6l5 7H43z" fill="white"/><path d="M1 16h19M0 22h15" stroke="white" stroke-width="3"/></svg>';
  const shield='<svg viewBox="0 0 48 52" fill="none" aria-hidden="true"><path d="M24 3 43 10v14c0 13-19 25-19 25S5 37 5 24V10L24 3Z" stroke="currentColor" stroke-width="2.5"/><path d="m15 25 6 6 13-14" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const lock='<svg class="n22-lock" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="5" y="10" width="14" height="11" rx="2" fill="currentColor"/><path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="15" r="1.5" fill="white"/></svg>';
  const freeCount=r=>Number(r.title.match(/\+\s*(\d+)\s*gratis/i)?.[1]||0);
  const unlockText=r=>freeCount(r)>0?`Beim ${r.qty-freeCount(r)}+${freeCount(r)}-Angebot`:`Ab ${r.qty} Seifen`;
  function card(kind,visual,title,note,unlocked=true){
    return `<div class="n22-benefit ${unlocked?'':'is-locked'}" data-benefit-kind="${kind}" data-unlocked="${unlocked}"><div class="n22-benefit-icon n22-reward-icon"><span class="n22-reward-visual">${visual}</span>${unlocked?'':lock}</div><strong>${title}</strong><small>${note}</small></div>`;
  }
  function render(panel,offer,row,base='assets/'){
    const hasTray=offer.rows.some(r=>r.tray);
    const cards=[card('shipping',truck,'Kostenloser<br>DHL Versand','Bei jedem Paket')];
    const maxDays=Math.max(...offer.rows.map(r=>r.days));
    const guaranteeThreshold=offer.rows.find(r=>r.days===maxDays);
    const hasGuarantee=row.days>=maxDays;
    cards.push(card('guarantee',shield,`${maxDays} Tage<br>Zufriedenheitsgarantie`,hasGuarantee?'In Deinem Paket':unlockText(guaranteeThreshold),hasGuarantee));
    if(hasTray){
      const threshold=offer.rows.find(r=>r.tray);
      cards.push(card('tray',`<img src="${base}product-tray.png" alt="Bambus-Seifenschale">`,'GRATIS<br>Bambus-Seifenschale',row.tray?'In Deinem Paket':unlockText(threshold),row.tray));
    }else{
      const freeThreshold=offer.rows.find(r=>freeCount(r)>0);
      if(freeThreshold){
        const count=freeCount(row)||freeCount(freeThreshold),included=freeCount(row)>0;
        cards.push(card('free-soap',`<img src="${base}product-1.png" alt="Nylura Kaki-Seife">`,`${count} ${count===1?'Seife':'Seifen'}<br>gratis`,included?'In Deinem Paket':unlockText(freeThreshold),included));
      }
    }
    panel.dataset.selection=row.key;
    panel.innerHTML='<span class="n22-benefit-legend">DEINE VORTEILE</span><div class="n22-benefit-grid" style="grid-template-columns:repeat('+cards.length+',minmax(0,1fr))">'+cards.join('')+'</div>';
  }
  return {render};
})();

(()=>{if(window.__nyluraSevenReady)return;window.__nyluraSevenReady=true;
const spec={"date": "2026-10-22", "heading": "Kostenloser DHL Versand", "offers": [{"id": "A", "name": "Aktuelles Offer · Kontrolle", "rows": [{"qty": 2, "cents": 2990, "days": 30, "tray": false, "default": false, "title": "2 Seifen", "explicit_guarantee": false, "key": "n22-a-1", "image": "https://cdn.shopify.com/s/files/1/0819/5774/8953/files/nylura-v7-2.png?v=1792248622", "compare_cents": 4499, "badge": ""}, {"qty": 3, "cents": 3999, "days": 30, "tray": false, "default": false, "title": "3 Seifen", "explicit_guarantee": false, "key": "n22-a-2", "image": "https://cdn.shopify.com/s/files/1/0819/5774/8953/files/nylura-v7-3.png?v=1792248622", "compare_cents": 5999, "badge": ""}, {"qty": 6, "cents": 5994, "days": 60, "tray": true, "default": true, "title": "6 Seifen", "explicit_guarantee": false, "key": "n22-a-3", "image": "https://cdn.shopify.com/s/files/1/0819/5774/8953/files/nylura-v7-6.png?v=1792248622", "compare_cents": 9999, "badge": "MEIST GEKAUFT"}, {"qty": 10, "cents": 8990, "days": 60, "tray": true, "default": false, "title": "10 Seifen", "explicit_guarantee": false, "key": "n22-a-4", "image": "https://cdn.shopify.com/s/files/1/0819/5774/8953/files/nylura-v7-10.png?v=1792248622", "compare_cents": 15999, "badge": "BESTER PREIS"}], "price_mode": "unit"}, {"id": "B", "name": "Vorrat & steigende Garantie", "rows": [{"qty": 1, "cents": 2990, "days": 30, "tray": false, "default": false, "title": "1 Seife", "explicit_guarantee": true, "key": "n22-b-1", "image": "https://cdn.shopify.com/s/files/1/0819/5774/8953/files/nylura-live-look-1x-v3_800x800.png?v=1792225848", "compare_cents": 3999, "badge": ""}, {"qty": 2, "cents": 3999, "days": 30, "tray": false, "default": false, "title": "2 Seifen", "explicit_guarantee": true, "key": "n22-b-2", "image": "https://cdn.shopify.com/s/files/1/0819/5774/8953/files/nylura-v7-2.png?v=1792248622", "compare_cents": 4499, "badge": ""}, {"qty": 4, "cents": 5999, "days": 60, "tray": false, "default": false, "title": "4 Seifen", "explicit_guarantee": true, "key": "n22-b-3", "image": "https://cdn.shopify.com/s/files/1/0819/5774/8953/files/nylura-v7-4.png?v=1792248622", "compare_cents": 7999, "badge": ""}, {"qty": 6, "cents": 6999, "days": 90, "tray": true, "default": true, "title": "6 Seifen", "explicit_guarantee": true, "key": "n22-b-4", "image": "https://cdn.shopify.com/s/files/1/0819/5774/8953/files/nylura-v7-6.png?v=1792248622", "compare_cents": 9999, "badge": "MEIST GEKAUFT"}], "price_mode": "unit"}, {"id": "C", "name": "Kleine Pakete & Garantie", "rows": [{"qty": 1, "cents": 2999, "days": 30, "tray": false, "default": false, "title": "1 Seife", "explicit_guarantee": true, "key": "n22-c-1", "image": "https://cdn.shopify.com/s/files/1/0819/5774/8953/files/nylura-live-look-1x-v3_800x800.png?v=1792225848", "compare_cents": 3999, "badge": ""}, {"qty": 2, "cents": 3999, "days": 60, "tray": false, "default": true, "title": "2 Seifen", "explicit_guarantee": true, "key": "n22-c-2", "image": "https://cdn.shopify.com/s/files/1/0819/5774/8953/files/nylura-v7-2.png?v=1792248622", "compare_cents": 4499, "badge": "MEIST GEKAUFT"}, {"qty": 3, "cents": 4999, "days": 90, "tray": true, "default": false, "title": "3 Seifen", "explicit_guarantee": true, "key": "n22-c-3", "image": "https://cdn.shopify.com/s/files/1/0819/5774/8953/files/nylura-v7-3.png?v=1792248622", "compare_cents": 5999, "badge": "BESTER PREIS"}], "price_mode": "unit"}, {"id": "D", "name": "Kaufe mehr · erhalte mehr", "rows": [{"qty": 1, "cents": 2990, "days": 30, "tray": false, "default": false, "title": "Kaufe 1", "explicit_guarantee": false, "key": "n22-d-1", "image": "https://cdn.shopify.com/s/files/1/0819/5774/8953/files/nylura-live-look-1x-v3_800x800.png?v=1792225848", "compare_cents": 3999, "badge": ""}, {"qty": 3, "cents": 3999, "days": 30, "tray": false, "default": false, "title": "Kaufe 2 + 1 gratis", "explicit_guarantee": false, "key": "n22-d-2", "image": "https://cdn.shopify.com/s/files/1/0819/5774/8953/files/nylura-v7-3.png?v=1792248622", "compare_cents": 5999, "badge": ""}, {"qty": 6, "cents": 5994, "days": 60, "tray": true, "default": true, "title": "Kaufe 3 + 3 gratis", "explicit_guarantee": false, "key": "n22-d-3", "image": "https://cdn.shopify.com/s/files/1/0819/5774/8953/files/nylura-v7-6.png?v=1792248622", "compare_cents": 9999, "badge": "MEIST GEKAUFT"}, {"qty": 10, "cents": 8990, "days": 90, "tray": true, "default": false, "title": "Kaufe 5 + 5 gratis", "explicit_guarantee": false, "key": "n22-d-4", "image": "https://cdn.shopify.com/s/files/1/0819/5774/8953/files/nylura-v7-10.png?v=1792248622", "compare_cents": 15999, "badge": "BESTER PREIS"}], "price_mode": "total"}, {"id": "E", "name": "1 + 1 · 2 + 2 · 3 + 3", "rows": [{"qty": 2, "cents": 3990, "days": 30, "tray": false, "default": false, "title": "Kaufe 1 + 1 gratis", "explicit_guarantee": false, "key": "n22-e-1", "image": "https://cdn.shopify.com/s/files/1/0819/5774/8953/files/nylura-v7-2.png?v=1792248622", "compare_cents": 4499, "badge": ""}, {"qty": 4, "cents": 5990, "days": 60, "tray": false, "default": true, "title": "Kaufe 2 + 2 gratis", "explicit_guarantee": false, "key": "n22-e-2", "image": "https://cdn.shopify.com/s/files/1/0819/5774/8953/files/nylura-v7-4.png?v=1792248622", "compare_cents": 7999, "badge": "MEIST GEKAUFT"}, {"qty": 6, "cents": 6990, "days": 90, "tray": true, "default": false, "title": "Kaufe 3 + 3 gratis", "explicit_guarantee": true, "key": "n22-e-3", "image": "https://cdn.shopify.com/s/files/1/0819/5774/8953/files/nylura-v7-6.png?v=1792248622", "compare_cents": 9999, "badge": "BESTER PREIS"}], "price_mode": "total"}, {"id": "F", "name": "Eine gratis zum gleichen Preis", "rows": [{"qty": 1, "cents": 2990, "days": 30, "tray": false, "default": false, "title": "Kaufe 1", "explicit_guarantee": false, "key": "n22-f-1", "image": "https://cdn.shopify.com/s/files/1/0819/5774/8953/files/nylura-live-look-1x-v3_800x800.png?v=1792225848", "compare_cents": 3999, "badge": ""}, {"qty": 2, "cents": 2990, "days": 60, "tray": false, "default": true, "title": "Kaufe 1 + 1 gratis", "explicit_guarantee": true, "key": "n22-f-2", "image": "https://cdn.shopify.com/s/files/1/0819/5774/8953/files/nylura-v7-2.png?v=1792248622", "compare_cents": 4499, "badge": "1 SEIFE GRATIS"}], "price_mode": "total"}], "comparison_ladder_cents": {"1": 3999, "2": 4499, "3": 5999, "4": 7999, "6": 9999, "10": 15999}, "notes": ["Neue Nutzerkorrektur: größere Badges MEIST GEKAUFT / BESTER PREIS, engere Zeilen; Deine Vorteile bleibt.", "Einstiegsgarantien 30 Tage; vollständige Matrix A30/30/60/60, B30/30/60/90, C30/60/90, D30/30/60/60, E30/60/90, F30/60. E-Mittelstufe60 ist abgeleiteter Entwurf.", "Vergleichswerte sind geplante Referenzpreise, keine verifizierten historischen Verkaufspreise. Einzel39,99 aus vorhandener Shopify-Preisbasis; 4er79,99 als Vorschlag.", "Meist gekauft ist angeforderter Badge-Entwurf. Beobachtete Käufe stützen nur6er im bisherigen Offer, keine Bestseller-Aussage für ungetestete Pakete.", "22.10.2026: Offer 1–3 Preis pro Seife, Offer 4–6 Gesamtpreis. Keine frei wechselnde Preisansicht mehr.", "22.10.2026: Nur Offer 1 als 50/50-Test mit Versandschutz versus ohne buchbaren Versandschutz. Weitere Offers unverändert mit Versandschutz.", "22.10.2026: Alle Garantiekacheln zeigen die längste im jeweiligen Offer erreichbare Garantie. Schloss bis zur passenden Paketstufe; tatsächliche Paketgarantie bleibt in der Zeile.", "Nur lokale V3-Abnahme; gespeicherter Mojo-Stand V1 und Live-Shop unverändert."], "revision": "v3-preisaufteilung-versandschutztest-2026-10-22", "price_test": {"status": "superseded", "replacement": "Feste Preisanzeige pro Offer; nur Offer 1 bekommt einen Versandschutz-Split-Test."}, "visual_colors": {"cta": "#6F847A", "most_bought_badge": "#6F847A", "best_price_badge": "#6F847A", "badge_text": "#FFFFFF"}, "benefit_display": {"tray_scope": "only_if_offer_contains_tray", "no_tray_offer": "highest_available_guarantee_and_actual_free_soap", "unlock_label": "Beim 1+1-Angebot", "source": "Nutzerkorrektur 22.10.2026", "guarantee": "highest_days_in_current_offer_locked_until_eligible_tier_selected"}, "protection_test": {"offer": "A", "status": "review_only_not_active", "arms": [{"id": "with", "weight": 50, "available": true, "default_selected": true, "cents": 390}, {"id": "without", "weight": 50, "available": false, "default_selected": false, "cents": 0}], "scope": "50/50 innerhalb von Offer 1; Gesamtverteilung zwischen Offers noch nicht festgelegt."}, "cta_price": false},parent='cmvjpyi5d09liobkpn3zyepcz',protect='50660599922905',flags=new WeakMap(),defaults=new WeakMap(),bypass=new WeakSet();let pending=false;
const money=n=>(n/100).toLocaleString('de-DE',{style:'currency',currency:'EUR'});
function current(){return [...document.querySelectorAll('.mojo-sf')].find(r=>r.dataset.offerId===parent);}
function noProtection(r){return r?.__mojoSettings?.stickyAtcTitle==='{{product}} · ohne Versandschutz';}
function refresh(){pending=false;const r=current();if(!r)return;r.classList.add('n22-live');const ready=!!r.__mojoSettings&&!!r.querySelector('[data-tier-key^="n22-"]');
for(const c of document.querySelectorAll('[data-cart-protection]')){if(!defaults.has(c))defaults.set(c,c.dataset.cartProtectionDefault);c.dataset.cartProtectionDefault=!ready||noProtection(r)?'false':defaults.get(c);c.hidden=ready&&noProtection(r);if(c.hidden)c.querySelector('input')?.setAttribute('aria-hidden','true');else c.querySelector('input')?.removeAttribute('aria-hidden');}
if(!ready)return;const els=[...r.querySelectorAll('[data-tier-key]')],prefix=els[0]?.dataset.tierKey?.match(/^n22-([a-f])-/)?.[1]?.toUpperCase(),offer=spec.offers.find(o=>o.id===prefix);if(!offer)return;
const selected=els.find(e=>e.classList.contains('is-selected')||e.classList.contains('mojo-sf-tier--selected'))||els[offer.rows.findIndex(x=>x.default)];const row=offer.rows.find(x=>x.key===selected?.dataset.tierKey);if(!row)return;
let panel=r.querySelector('.n22-benefits');const stamp=row.key+'|'+r.dataset.variantId; if(flags.get(r)===stamp&&panel&&els.every(e=>e.querySelector('.n22-reference-price')))return;
for(const e of els){const item=offer.rows.find(x=>x.key===e.dataset.tierKey),p=e.querySelector('.mojo-sf-tier-price-current');if(!item||!p)continue;const unit=offer.price_mode==='unit';p.innerHTML='<span class="n22-price-value">'+money(unit?Math.ceil(item.cents/item.qty):item.cents)+'</span><small class="n22-price-caption">'+(unit?'pro Seife':'gesamt')+'</small><s class="n22-reference-price">'+money(unit?Math.ceil(item.compare_cents/item.qty):item.compare_cents)+'</s>';}
if(!panel){panel=document.createElement('div');panel.className='n22-benefits';r.querySelector('.mojo-sf-tiers')?.after(panel);}const assets='https://cdn.shopify.com/s/files/1/0819/5774/8953/files/';window.NyluraSevenBenefits.render(panel,offer,row,assets);panel.querySelectorAll('img').forEach(img=>img.src=img.src.includes('product-tray')?assets+'nylura-v7-tray.png?v=1792248622':spec.offers[1].rows[0].image);flags.set(r,stamp);}
function schedule(){if(!pending){pending=true;requestAnimationFrame(refresh);}}
async function json(path,body){const res=await fetch((window.Shopify?.routes?.root||'/')+path,{method:body?'POST':'GET',headers:{'Content-Type':'application/json','Accept':'application/json'},...(body?{body:JSON.stringify(body)}:{})});if(!res.ok)throw Error('Deine Auswahl konnte gerade nicht gespeichert werden. Bitte versuche es erneut.');return res.json();}
document.addEventListener('submit',async event=>{const r=current(),form=event.target;if(!r||!noProtection(r)||!(form.contains(r)||form.matches('[data-nylura-mojo-form]')))return;if(bypass.has(form)){bypass.delete(form);return;}event.preventDefault();event.stopImmediatePropagation();const submitter=event.submitter;try{let cart=await json('cart.js');for(const item of cart.items.filter(x=>String(x.variant_id)===protect))await json('cart/change.js',{id:item.key,quantity:0});await json('cart/update.js',{attributes:{nylura_shipping_protection:'off'}});bypass.add(form);form.requestSubmit(submitter||undefined);}catch(error){let e=form.querySelector('[data-n22-error]');if(!e){e=document.createElement('p');e.dataset.n22Error='';e.setAttribute('role','alert');form.append(e);}e.textContent=error.message;}},true);
new MutationObserver(schedule).observe(document.documentElement,{childList:true,subtree:true,attributes:true,attributeFilter:['class','data-variant-id']});document.addEventListener('change',schedule);document.addEventListener('click',schedule);schedule();})();